import classNames from 'classnames';
import React, { CSSProperties, SVGProps, useCallback, useEffect, useState } from 'react';

interface ISvgIconProps {
  IconComponent: React.FC<SVGProps<SVGSVGElement>>;
  className?: string;
  wrapperClass?: string;
  width?: string;
  height?: string;
  defaultFill?: string;
  hoverFill?: string;
  activeFill?: string;
  defaultBackground?: string;
  hoverBackground?: string;
  activeBackground?: string;
  stroke?: string;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
}

type IconProps = {
  background?: string;
  fill?: string;
  width?: string;
  height?: string;
  stroke?: string;
};

export const SvgIcon: React.FC<ISvgIconProps> = ({
  IconComponent,
  defaultFill,
  hoverFill = defaultFill,
  activeFill = defaultFill,
  defaultBackground = 'transparent',
  hoverBackground = defaultBackground,
  activeBackground = defaultBackground,
  width,
  height,
  className = '',
  wrapperClass,
  stroke,
  onClick,
}) => {
  const [fill, setFill] = useState(defaultFill);
  const [background, setBackground] = useState(defaultBackground);

  const handleMouseEnter = useCallback(() => {
    setFill(hoverFill);
    setBackground(hoverBackground);
  }, [hoverFill, hoverBackground]);

  const handleMouseLeave = useCallback(() => {
    setFill(defaultFill);
    setBackground(defaultBackground);
  }, [defaultFill, defaultBackground]);

  const handleMouseDown = useCallback(() => {
    setFill(activeFill);
    setBackground(activeBackground);
  }, [activeFill, activeBackground]);

  const handleMouseUp = useCallback(() => {
    setFill(hoverFill);
    setBackground(hoverBackground);
  }, [hoverFill, hoverBackground]);

  useEffect(() => {
    setFill(defaultFill);
  }, [defaultFill]);

  if (!IconComponent) {
    return null;
  }

  const iconProps: IconProps = {
    background,
    fill,
    stroke,
  };
  const divStyle: CSSProperties = {};
  if (width !== undefined) {
    iconProps.width = width;
    divStyle.maxWidth = width;
  }
  if (height !== undefined) {
    iconProps.height = height;
    divStyle.maxHeight = height;
  }
  divStyle.verticalAlign = 'sub';
  divStyle.lineHeight = 'normal';

  if (wrapperClass) {
    return (
      <span className={classNames('inline-flex justify-center items-center', wrapperClass)}>
        <div
          className={className}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          style={divStyle}
          onClick={onClick}
        >
          <IconComponent {...iconProps} />
        </div>
      </span>
    );
  }

  return (
    <div
      className={`inline-flex items-center justify-center ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      style={divStyle}
      onClick={onClick}
    >
      <IconComponent {...iconProps} />
    </div>
  );
};
